import { useEffect, useState } from "react";
import type { WritingGoals } from "@quill/shared";
import {
  claimCelebration,
  setGoals,
  useProgress,
  type GoalProgress,
  type GoalStatus,
  type Period,
} from "../progress";
import { Dialog } from "./Dialog";

const numberFormat = new Intl.NumberFormat();
const words = (n: number) => `${numberFormat.format(n)} ${Math.abs(n) === 1 ? "word" : "words"}`;
/** "+120" / "−40" (a real minus sign). */
const signed = (n: number) => (n < 0 ? `−${numberFormat.format(-n)}` : `+${numberFormat.format(n)}`);

const STATUS_LABEL: Record<GoalStatus, string> = {
  behind: "Behind",
  "on-track": "On track",
  ahead: "Ahead",
  met: "Goal met",
};

const PERIODS: { value: Period; label: string; hint: string }[] = [
  { value: "session", label: "Session", hint: "this session" },
  { value: "day", label: "1 day", hint: "in the last 24 hours" },
  { value: "week", label: "7 days", hint: "in the last 7 days" },
  { value: "month", label: "30 days", hint: "in the last 30 days" },
];

const PERIOD_KEY = "quill.progress.period";

function readPeriod(): Period {
  try {
    const v = localStorage.getItem(PERIOD_KEY);
    return PERIODS.some((p) => p.value === v) ? (v as Period) : "session";
  } catch {
    return "session";
  }
}

const goalLabel = (g: GoalProgress) => (g.kind === "daily" ? "Today" : "This week");
const describe = (g: GoalProgress) =>
  `${goalLabel(g)}: ${numberFormat.format(g.done)} of ${words(g.target)}, ${STATUS_LABEL[g.status].toLowerCase()}`;

/** A small ring: how far through the goal, coloured by pace. */
function Ring({ goal, size = 18 }: { goal: GoalProgress; size?: number }) {
  const r = 7;
  const c = 2 * Math.PI * r;
  const filled = Math.min(1, Math.max(0, goal.done / goal.target));
  return (
    <svg className={`goal-ring ${goal.status}`} width={size} height={size} viewBox="0 0 18 18" aria-hidden="true">
      <circle className="track" cx="9" cy="9" r={r} />
      <circle className="fill" cx="9" cy="9" r={r} strokeDasharray={`${filled * c} ${c}`} transform="rotate(-90 9 9)" />
    </svg>
  );
}

/**
 * Status line button: session words and a ring for today's goal (or the week's, with no
 * daily goal). Celebrates the first time a goal is met in its day or week.
 */
export function ProgressButton({ onOpen }: { onOpen(): void }) {
  const progress = useProgress();
  const goal = progress.daily ?? progress.weekly;
  const [cheer, setCheer] = useState<GoalProgress | null>(null);

  useEffect(() => {
    for (const g of [progress.daily, progress.weekly]) {
      if (g && claimCelebration(g)) {
        setCheer(g);
        break;
      }
    }
  }, [progress.daily, progress.weekly]);
  useEffect(() => {
    if (!cheer) return;
    const t = setTimeout(() => setCheer(null), 4000);
    return () => clearTimeout(t);
  }, [cheer]);

  const session = progress.written.session;
  const label = [`${words(session)} this session`, ...[progress.daily, progress.weekly].filter((g) => !!g).map(describe)].join(". ");

  return (
    <>
      <button
        type="button"
        className={`progress-button${cheer ? " celebrate" : ""}`}
        aria-label={`Progress. ${label}`}
        title={label}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onOpen}
      >
        {goal && (
          <span className="ring-host">
            <Ring goal={goal} />
            {cheer && (
              <span className="burst" aria-hidden="true">
                {Array.from({ length: 10 }, (_, i) => (
                  <i key={i} style={{ "--i": i } as React.CSSProperties} />
                ))}
              </span>
            )}
          </span>
        )}
        <span className="session-words">{signed(session)}</span>
      </button>
      {cheer && (
        <p className="goal-toast" role="status">
          {cheer.kind === "daily" ? "Daily" : "Weekly"} goal met: {words(cheer.done)}. Well done!
        </p>
      )}
    </>
  );
}

/** Words written per period, progress towards each goal, and the goals themselves. */
export function ProgressSheet({ onClose }: { onClose(): void }) {
  const progress = useProgress();
  const [period, setPeriod] = useState<Period>(readPeriod);
  const choose = (p: Period) => {
    setPeriod(p);
    try {
      localStorage.setItem(PERIOD_KEY, p);
    } catch {
      // Not remembered; fine.
    }
  };
  const n = progress.written[period];
  const goals = [progress.daily, progress.weekly].filter((g): g is GoalProgress => !!g);
  const sessionSince = new Date(progress.sessionStartedAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

  return (
    <Dialog title="Progress" onClose={onClose}>
      <fieldset className="segmented">
        <legend>Words written</legend>
        {PERIODS.map((p) => (
          <label key={p.value}>
            <input type="radio" name="period" value={p.value} checked={period === p.value} onChange={() => choose(p.value)} />
            <span>{p.label}</span>
          </label>
        ))}
      </fieldset>
      <p className="written-total" aria-live="polite">
        <strong>{numberFormat.format(n)}</strong> {Math.abs(n) === 1 ? "word" : "words"}{" "}
        <span className="hint">
          {period === "session" && n ? `since ${sessionSince}` : PERIODS.find((p) => p.value === period)!.hint}
        </span>
      </p>

      {goals.length > 0 && (
        <ul className="goal-list" aria-label="Goals">
          {goals.map((g) => (
            <li key={g.kind}>
              <div className="goal-head">
                <span>{goalLabel(g)}</span>
                <span className={`goal-status ${g.status}`}>{STATUS_LABEL[g.status]}</span>
              </div>
              <div
                className={`goal-bar ${g.status}`}
                role="progressbar"
                aria-label={`${goalLabel(g)}'s goal`}
                aria-valuemin={0}
                aria-valuemax={g.target}
                aria-valuenow={Math.max(0, Math.min(g.done, g.target))}
                aria-valuetext={describe(g)}
              >
                <span style={{ width: `${Math.min(100, Math.max(0, (g.done / g.target) * 100))}%` }} />
              </div>
              <p className="hint">
                {numberFormat.format(g.done)} of {words(g.target)}
                {g.done < g.target ? ` · ${numberFormat.format(g.target - g.done)} to go` : ""}
              </p>
            </li>
          ))}
        </ul>
      )}

      <details className="settings-goals" open={goals.length === 0}>
        <summary>Goals</summary>
        <GoalFields />
      </details>

      <p className="hint progress-note">
        Net words across all your documents: deleting words counts against them. Restoring a version doesn't count.
      </p>

      <div className="sheet-actions">
        <button className="primary" onClick={onClose}>
          Done
        </button>
      </div>
    </Dialog>
  );
}

/** Daily and weekly word targets; empty means no goal. Saved when a field is left. */
export function GoalFields() {
  const { goals } = useProgress();
  return (
    <div className="goal-fields">
      <GoalInput label="Daily goal" field="daily" goals={goals} />
      <GoalInput label="Weekly goal" field="weekly" goals={goals} />
    </div>
  );
}

function GoalInput({ label, field, goals }: { label: string; field: keyof WritingGoals; goals: WritingGoals }) {
  const current = goals[field];
  const [value, setValue] = useState(current?.toString() ?? "");
  useEffect(() => setValue(current?.toString() ?? ""), [current]);

  const save = () => {
    const parsed = Math.round(Number(value.replace(/[^\d]/g, "")));
    const next = value.trim() && parsed > 0 ? Math.min(parsed, 1_000_000) : null;
    setValue(next?.toString() ?? "");
    if (next !== current) setGoals({ ...goals, [field]: next });
  };

  return (
    <label className="goal-input">
      <span>{label}</span>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="None"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
      />
      <span className="hint">words</span>
    </label>
  );
}
